import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ZapIcon, MessageSquareIcon, PhoneIcon, CalendarIcon, DatabaseIcon, CheckCircleIcon, XCircleIcon, ClockIcon, ArrowRightIcon, SmartphoneIcon, GlobeIcon, MailIcon, ChevronDownIcon, BuildingIcon } from 'lucide-react';
import { SEOHead } from '../components/SEOHead';
import { ScrollReveal } from '../components/ScrollReveal';
export function AutomationsPage() {
  const faqs = [{
    question: 'How quickly does the AI respond to leads?',
    answer: 'Our AI systems respond to text messages, lead platform inquiries, and web forms within seconds, 24/7. For missed calls, the AI text-back feature triggers immediately after the call ends.'
  }, {
    question: 'Will it sound like a robot?',
    answer: 'No. We train the AI on your specific business information, services, and tone of voice. It sounds natural, helpful, and professional - most customers never realize they are speaking with AI.'
  }, {
    question: 'Can it actually book appointments?',
    answer: 'Yes. The AI integrates directly with your calendar. It can qualify the lead, answer basic questions, and provide a booking link or directly schedule the appointment based on your real-time availability.'
  }, {
    question: 'What happens if the AI does not know the answer?',
    answer: "If a customer asks a complex question outside the AI's training, it gracefully transitions the conversation. It will notify your team immediately and let the customer know a human will follow up shortly."
  }, {
    question: 'Do I need to change my current phone number?',
    answer: 'No. We can port your existing number into our system, or provide a new tracking number that forwards to your main line while enabling all the AI text-back features.'
  }, {
    question: 'Do you build AI receptionists for landscapers, painters, and other home service businesses?',
    answer: 'Yes. Our AI Lead Conversion system is built specifically for home service operators - landscapers, painters, HVAC, plumbing, roofing, and pest control - who lose jobs when they cannot answer the phone from a jobsite. It answers calls and messages, qualifies the lead, and books the estimate automatically.'
  }];
  return <div className="pt-24 pb-16" data-id="element-518" data-mp="166cnxe6d8lum">
      <SEOHead title="AI Receptionist & Missed Call Text-Back Phoenix | Viridia Analytics" description="AI receptionist and missed call text-back for Phoenix home service businesses. Never miss a lead - our AI answers calls, texts, and web forms in seconds and books the job." canonical="/ai-lead-conversion" keywords="AI marketing agency phoenix, AI advertising, AI lead conversion, speed to lead, missed call text back, AI receptionist, HVAC, painters, landscapers, contractors, window installers, Phoenix local business leads" faq={faqs} data-id="element-519" data-mp="167487suiix5l" />

      {/* Hero Section */}
      <section className="relative pt-12 pb-20 overflow-hidden" data-id="element-520" data-mp="3egc2wx3w5ii0">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none" data-id="element-521" data-mp="39ecz5m48ojkj">
          <div className="absolute top-[-10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-viridia/10 blur-[120px] animate-pulse-slow" data-id="element-522" data-mp="3ndmigunqrtsq"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full" data-id="element-523" data-mp="2upz07m9y1f99">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-524" data-mp="2unw36iadilv1">
            <ScrollReveal data-id="element-525" data-mp="2qq7ywomsjgqh">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-viridia/10 border border-viridia/20 text-viridia text-sm font-medium mb-6" data-id="element-526" data-mp="2qmu17tp2nvpd">
                <ZapIcon className="w-4 h-4" data-id="element-527" data-mp="2zns5md3fa58h" />
                <span data-id="element-528" data-mp="2bzha7pghwxrl">Flagship System</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4" data-id="element-529" data-mp="2bxxuq12nyqqv">
                AI Receptionist & Missed Call Text-Back <br data-id="element-530" data-mp="2knwoaz5u60vh" />
                <span className="text-viridia" data-id="element-531" data-mp="2kofsze6h6srk">for Home Service Businesses</span>
              </h1>
              <p className="text-xl text-viridia font-semibold mb-6 max-w-2xl" data-id="element-532" data-mp="2pnizl323t71n">
                Never miss a lead. Book jobs faster.
              </p>
              <p className="text-xl text-gray-400 mb-4 max-w-2xl" data-id="element-533" data-mp="2ujpw658m166x">
                When a homeowner needs a service, they hire the first contractor
                that responds. Our AI receptionist and missed call text-back
                system answers inbound messages, missed calls, and web forms in
                seconds - qualifying prospects and booking estimates 24/7.
              </p>
              <p className="text-lg text-gray-400 font-medium mb-8 max-w-2xl" data-id="element-534" data-mp="3da7o47lmbxr8">
                Stop wasting ad spend on leads that go cold while you are on a
                jobsite.
              </p>
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-535" data-mp="1j0drdbz9x978">
                <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-base font-medium text-white bg-viridia rounded-lg hover:bg-viridia-dark transition-all duration-300 hover:shadow-[0_0_20px_rgba(16,185,129,0.4)]" data-id="element-536" data-mp="1nxnntbl2yl4k">
                  See Where Your Leads Are Leaking
                </Link>
                <Link to="/pricing" className="inline-flex items-center justify-center px-8 py-4 text-base font-medium text-white bg-viridia-surface border border-gray-700 rounded-lg hover:bg-gray-800 transition-colors" data-id="element-537" data-mp="3nh1uqxmqw4ws">
                  View Packages
                </Link>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} className="hidden lg:block" data-id="element-538" data-mp="a1cj791p2ccb">
              <div className="relative" data-id="element-539" data-mp="a36udvn5yq37">
                {/* Abstract visualization of leads flowing into booked jobs */}
                <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-6 relative overflow-hidden" data-id="element-540" data-mp="2urbhsrwun929">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-viridia/5 rounded-full blur-3xl" data-id="element-541" data-mp="2w7yvwwyzy36o"></div>

                  <div className="flex flex-col gap-6 relative z-10" data-id="element-542" data-mp="317tn71moous9">
                    {/* Incoming Leads */}
                    <div className="flex justify-between items-center gap-4" data-id="element-543" data-mp="2n70u8lc167r9">
                      <motion.div initial={{
                      opacity: 0,
                      x: -20
                    }} animate={{
                      opacity: 1,
                      x: 0
                    }} transition={{
                      duration: 0.5,
                      delay: 0.5
                    }} className="flex-1 bg-viridia-black border border-gray-800 rounded-xl p-4 flex items-center gap-3" data-id="element-544" data-mp="2s63xq1cd45x4">
                        <div className="w-10 h-10 rounded-full bg-[#FF1A1A]/10 flex items-center justify-center flex-shrink-0" data-id="element-545" data-mp="1zirb5r67by3t">
                          <MessageSquareIcon className="w-5 h-5 text-[#FF1A1A]" data-id="element-546" data-mp="1fx7m84jh8y4c" />
                        </div>
                        <div data-id="element-547" data-mp="1ayweq0gnmzpa">
                          <div className="text-xs text-gray-500" data-id="element-548" data-mp="1pum1w1xktivl">
                            Lead Request
                          </div>
                          <div className="text-sm font-medium text-white truncate" data-id="element-549" data-mp="1kuhjg6d3ye2q">
                            "Need a quote for..."
                          </div>
                        </div>
                      </motion.div>

                      <motion.div initial={{
                      opacity: 0,
                      x: -20
                    }} animate={{
                      opacity: 1,
                      x: 0
                    }} transition={{
                      duration: 0.5,
                      delay: 0.7
                    }} className="flex-1 bg-viridia-black border border-gray-800 rounded-xl p-4 flex items-center gap-3" data-id="element-550" data-mp="1tvxi5guk8rr4">
                        <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0" data-id="element-551" data-mp="3ownzobo5i2sg">
                          <PhoneIcon className="w-5 h-5 text-blue-500" data-id="element-552" data-mp="3tvhgoe2ncbxd" />
                        </div>
                        <div data-id="element-553" data-mp="1r6t5rr39f2z">
                          <div className="text-xs text-gray-500" data-id="element-554" data-mp="3196ex03b04yz">
                            Missed Call
                          </div>
                          <div className="text-sm font-medium text-white truncate" data-id="element-555" data-mp="36898nodu2orn">
                            From (602) 555-...
                          </div>
                        </div>
                      </motion.div>
                    </div>

                    {/* AI Processing */}
                    <div className="flex justify-center py-2" data-id="element-556" data-mp="2ihnff38hn91p">
                      <motion.div animate={{
                      y: [0, 5, 0]
                    }} transition={{
                      duration: 2,
                      repeat: Infinity
                    }} className="w-12 h-12 rounded-full bg-viridia/20 border border-viridia/50 flex items-center justify-center" data-id="element-557" data-mp="2mj8kw3ip0l1v">
                        <ZapIcon className="w-6 h-6 text-viridia" data-id="element-558" data-mp="20zyb0vxu3gxv" />
                      </motion.div>
                    </div>

                    {/* Instant Responses */}
                    <div className="space-y-3" data-id="element-559" data-mp="1r9ludet547x0">
                      <motion.div initial={{
                      opacity: 0,
                      y: 20
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} transition={{
                      duration: 0.5,
                      delay: 1.2
                    }} className="bg-viridia/5 border border-viridia/20 rounded-xl p-4 flex items-start gap-3 ml-8" data-id="element-560" data-mp="1r8tz653ggc79">
                        <div className="w-8 h-8 rounded-full bg-viridia flex items-center justify-center flex-shrink-0 mt-1" data-id="element-561" data-mp="25rmj2fipqzx7">
                          <ZapIcon className="w-4 h-4 text-white" data-id="element-562" data-mp="2ek6gjcn9qygg" />
                        </div>
                        <div data-id="element-563" data-mp="1qum0b1ii2fav">
                          <div className="text-xs text-viridia font-medium mb-1" data-id="element-564" data-mp="1qtu4bpkzqfk2">
                            Instant AI Response (0.5s)
                          </div>
                          <div className="text-sm text-gray-300" data-id="element-565" data-mp="20ss09dkqxbd3">
                            "Hi! We can definitely help with that. Are you
                            looking to get this done this week?"
                          </div>
                        </div>
                      </motion.div>

                      <motion.div initial={{
                      opacity: 0,
                      y: 20
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} transition={{
                      duration: 0.5,
                      delay: 1.8
                    }} className="bg-viridia-black border border-gray-800 rounded-xl p-4 flex items-start gap-3 mr-8" data-id="element-566" data-mp="378u6vmmvke0v">
                        <div className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-567" data-mp="1crfznuhnkede">
                          <SmartphoneIcon className="w-4 h-4 text-gray-400" data-id="element-568" data-mp="12zsda7zydnis" />
                        </div>
                        <div data-id="element-569" data-mp="17xk1os1e2ri8">
                          <div className="text-xs text-gray-500 mb-1" data-id="element-570" data-mp="17wsjqk0pssaf">
                            Customer
                          </div>
                          <div className="text-sm text-gray-300" data-id="element-571" data-mp="20eo306vitdcs">
                            "Yes, ideally Thursday."
                          </div>
                        </div>
                      </motion.div>

                      <motion.div initial={{
                      opacity: 0,
                      y: 20
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} transition={{
                      duration: 0.5,
                      delay: 2.4
                    }} className="bg-viridia/10 border border-viridia/30 rounded-xl p-4 flex items-center justify-between ml-8" data-id="element-572" data-mp="25b0jikxlw5f5">
                        <div className="flex items-center gap-3" data-id="element-573" data-mp="1cnegbdsaj2st">
                          <div className="w-10 h-10 rounded-full bg-viridia/20 flex items-center justify-center flex-shrink-0" data-id="element-574" data-mp="1cks06prc3eyz">
                            <CalendarIcon className="w-5 h-5 text-viridia" data-id="element-575" data-mp="13mg62dr9dz1s" />
                          </div>
                          <div data-id="element-576" data-mp="3fa31ysyfeu1a">
                            <div className="text-sm font-bold text-white" data-id="element-577" data-mp="3f908cd9pydho">
                              Appointment Booked
                            </div>
                            <div className="text-xs text-gray-400" data-id="element-578" data-mp="3o9q27bff5tsr">
                              Thursday @ 10:00 AM
                            </div>
                          </div>
                        </div>
                        <CheckCircleIcon className="w-6 h-6 text-viridia" data-id="element-579" data-mp="2vmc254gfci4f" />
                      </motion.div>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Speed to Lead Comparison */}
      <section className="py-20 bg-viridia-black" data-id="element-580" data-mp="3abi95eli7cmv">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-581" data-mp="3ac9w6111c21j">
          <ScrollReveal data-id="element-582" data-mp="2cujiw1r5qb9w">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-583" data-mp="2csym05hdeg8v">
              <h2 className="text-3xl font-bold text-white mb-4" data-id="element-584" data-mp="2hsurk95pufqj">
                The Financial Cost of a Slow Response
              </h2>
              <p className="text-gray-400" data-id="element-585" data-mp="nbkxssfwajz7">
                In local services, the first contractor to reply wins the job
                78% of the time. If you are on a roof, under a sink, or driving,
                you are losing money to competitors who answer instantly.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-586" data-mp="17155fzk8xm5n">
            <ScrollReveal data-id="element-587" data-mp="173rds1eyo5ox">
              <div className="bg-viridia-surface border border-red-900/30 rounded-2xl p-8 relative overflow-hidden h-full" data-id="element-588" data-mp="123d3ecpw0jnq">
                <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/5 rounded-full blur-3xl" data-id="element-589" data-mp="1g1juna0bzlef"></div>
                <div className="flex items-center gap-4 mb-6" data-id="element-590" data-mp="1b4tdejcsijrf">
                  <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center" data-id="element-591" data-mp="3htpyweyxhgil">
                    <ClockIcon className="w-6 h-6 text-red-500" data-id="element-592" data-mp="3cvejlopv6dsz" />
                  </div>
                  <div data-id="element-593" data-mp="1sd4549fbdbxm">
                    <h3 className="text-xl font-bold text-white" data-id="element-594" data-mp="1sdvn2hfznb5f">
                      The Old Way
                    </h3>
                    <p className="text-red-400 text-sm" data-id="element-595" data-mp="1xbf9i4muau8g">
                      Average response: 2-4 hours
                    </p>
                  </div>
                </div>
                <ul className="space-y-4" data-id="element-596" data-mp="1nl2jvy064avg">
                  <li className="flex items-start gap-3" data-id="element-597" data-mp="1njrfbvywez3a">
                    <XCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-598" data-mp="19ll1c0dgttuj" />
                    <span className="text-gray-300" data-id="element-599" data-mp="1emh42am2z9yd">
                      Miss a call while on a job site
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-600" data-mp="10o0ywo3v9ric">
                    <XCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-601" data-mp="14o1cy7n200ov" />
                    <span className="text-gray-300" data-id="element-602" data-mp="2v0qtz95z16rb">
                      Customer immediately calls the next business on the lead
                      platform
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-603" data-mp="buemyfmuj0av">
                    <XCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-604" data-mp="6t8soddjf4z8" />
                    <span className="text-gray-300" data-id="element-605" data-mp="2rvlnzypkvqq">
                      You call back 2 hours later
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-606" data-mp="3u0126uw80wer">
                    <XCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-607" data-mp="ujup17e6j0te" />
                    <span className="text-gray-300" data-id="element-608" data-mp="qhzpa7bwcvhq">
                      "Sorry, we already found someone."
                    </span>
                  </li>
                </ul>
                <div className="mt-8 p-4 bg-red-500/10 rounded-lg border border-red-500/20" data-id="element-609" data-mp="looejv3yrukj">
                  <p className="text-red-400 font-medium text-center" data-id="element-610" data-mp="gptvcs2343zw">
                    Result: Wasted ad spend & lost revenue
                  </p>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} data-id="element-611" data-mp="1grqfb6rynzbs">
              <div className="bg-viridia-surface border border-viridia/30 rounded-2xl p-8 relative overflow-hidden h-full" data-id="element-612" data-mp="1gsrxrwbb3o3j">
                <div className="absolute top-0 right-0 w-32 h-32 bg-viridia/10 rounded-full blur-3xl" data-id="element-613" data-mp="1lr396bh926nv"></div>
                <div className="flex items-center gap-4 mb-6" data-id="element-614" data-mp="1qs0sirdzx6qy">
                  <div className="w-12 h-12 rounded-full bg-viridia/20 flex items-center justify-center" data-id="element-615" data-mp="1uskq1w2hecdm">
                    <ZapIcon className="w-6 h-6 text-viridia" data-id="element-616" data-mp="1zpt7tvjpweec" />
                  </div>
                  <div data-id="element-617" data-mp="24onp2t21duwc">
                    <h3 className="text-xl font-bold text-white" data-id="element-618" data-mp="24nuq4e9bk9or">
                      The Viridia Way
                    </h3>
                    <p className="text-viridia text-sm" data-id="element-619" data-mp="2efin0uonaixm">
                      Average response: &lt; 5 seconds
                    </p>
                  </div>
                </div>
                <ul className="space-y-4" data-id="element-620" data-mp="2if0w8t73t5km">
                  <li className="flex items-start gap-3" data-id="element-621" data-mp="2nfnji9savwx0">
                    <CheckCircleIcon className="w-5 h-5 text-viridia flex-shrink-0 mt-0.5" data-id="element-622" data-mp="2ne440legxpwa" />
                    <span className="text-gray-300" data-id="element-623" data-mp="38t8943dy2ytw">
                      Miss a call while on a job site
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-624" data-mp="3ipkyip2giqiu">
                    <CheckCircleIcon className="w-5 h-5 text-viridia flex-shrink-0 mt-0.5" data-id="element-625" data-mp="3imyrqrne4f1o" />
                    <span className="text-gray-300" data-id="element-626" data-mp="3dodu8zziw9gx">
                      AI instantly texts: "Hi, sorry we missed you! How can we
                      help?"
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-627" data-mp="2zqgtdre1dgwg">
                    <CheckCircleIcon className="w-5 h-5 text-viridia flex-shrink-0 mt-0.5" data-id="element-628" data-mp="2zs0bzon6041e" />
                    <span className="text-gray-300" data-id="element-629" data-mp="2uyyv5b3eduf6">
                      Customer engages with AI instead of calling competitors
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-630" data-mp="22busvdvovu73">
                    <CheckCircleIcon className="w-5 h-5 text-viridia flex-shrink-0 mt-0.5" data-id="element-631" data-mp="2298m3ggmhipx" />
                    <span className="text-gray-300" data-id="element-632" data-mp="hr7motpkx3lh">
                      AI qualifies the lead and books the appointment
                    </span>
                  </li>
                </ul>
                <div className="mt-8 p-4 bg-viridia/10 rounded-lg border border-viridia/20" data-id="element-633" data-mp="7srvkqqjxubz">
                  <p className="text-viridia font-medium text-center" data-id="element-634" data-mp="crv08a2mdyjf">
                    Result: Captured lead & booked job
                  </p>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* How It Works - 4 Steps */}
      <section className="py-20 bg-viridia-surface" data-id="element-635" data-mp="rikghquxu3u5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-636" data-mp="vjnu06pijns7">
          <ScrollReveal data-id="element-637" data-mp="vm9x9u5dfh4m">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-638" data-mp="3c8ji7ile3gob">
              <h2 className="text-3xl font-bold text-white mb-4" data-id="element-639" data-mp="3cbfi3cgssffe">
                How the System Works
              </h2>
              <p className="text-gray-400" data-id="element-640" data-mp="2ik7ircwmx245">
                A seamless, automated pipeline that catches every opportunity
                and drives it toward a booked appointment.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-4 gap-8 relative" data-id="element-641" data-mp="28kyt2w8sy9nl">
            {/* Connecting line for desktop */}
            <div className="hidden md:block absolute top-1/2 left-0 w-full h-0.5 bg-gray-800 -translate-y-1/2 z-0" data-id="element-642" data-mp="2dj08n770rqub"></div>

            {[{
            step: '01',
            title: 'Lead Enters',
            desc: 'A customer calls, texts, fills out a form, or messages you through a lead platform.',
            icon: GlobeIcon
          }, {
            step: '02',
            title: 'Instant Response',
            desc: 'Within seconds, our AI receptionist acknowledges the inquiry and starts a conversation.',
            icon: ZapIcon
          }, {
            step: '03',
            title: 'Qualify & Nurture',
            desc: 'The AI asks qualifying questions to ensure they are a good fit for your services.',
            icon: MessageSquareIcon
          }, {
            step: '04',
            title: 'Book & Track',
            desc: 'The lead is booked on your calendar and tracked in your CRM pipeline.',
            icon: DatabaseIcon
          }].map((item, index) => <ScrollReveal key={index} delay={index * 0.1} data-id="element-643" data-mp="1g86csiyfwuz5" data-mpi={index}>
                <div className="relative z-10 bg-viridia-black border border-gray-800 rounded-2xl p-6 h-full flex flex-col" data-id="element-644" data-mp="1g5k42fztc9fc">
                  <div className="w-12 h-12 rounded-full bg-viridia-surface border border-gray-700 flex items-center justify-center mb-6 mx-auto" data-id="element-645" data-mp="1p089q79avt4o">
                    <item.icon className="w-5 h-5 text-viridia" data-id="element-646" data-mp="23xqwj42f3w9h" />
                  </div>
                  <div className="text-center flex-grow" data-id="element-647" data-mp="1yzfmotc4hlr9">
                    <div className="text-xs font-bold text-viridia mb-2" data-id="element-648" data-mp="261m7jaf3ojcx">
                      STEP {item.step}
                    </div>
                    <h3 className="text-lg font-bold text-white mb-3" data-id="element-649" data-mp="2b176vly6mtq6">
                      {item.title}
                    </h3>
                    <p className="text-sm text-gray-400" data-id="element-650" data-mp="2fxffshlsdqow">{item.desc}</p>
                  </div>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* Scenario Cards */}
      <section className="py-20 bg-viridia-black border-t border-gray-800" data-id="element-651" data-mp="2tqna4h1950aa">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-652" data-mp="2tsrltl5e73l4">
          <ScrollReveal data-id="element-653" data-mp="2yrb6li8929bw">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-654" data-mp="2ynx489zl604g">
              <h2 className="text-3xl font-bold text-white mb-4" data-id="element-655" data-mp="33nrz0omhf9us">
                Built for Real Scenarios
              </h2>
              <p className="text-gray-400" data-id="element-656" data-mp="14e3azocg9tp7">
                How our AI handles the situations that usually cost you money.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-657" data-mp="11t3uu063ylj4">
            <ScrollReveal data-id="element-658" data-mp="x0asfiy43pa7">
              <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-6 h-full" data-id="element-659" data-mp="x02ednq96dxf">
                <div className="text-sm text-viridia font-medium mb-4" data-id="element-660" data-mp="s2spc96s04a4">
                  Scenario 1: The After-Hours Lead
                </div>
                <p className="text-gray-300 text-sm mb-6" data-id="element-661" data-mp="n0tp386c1nhe">
                  A Yelp or Google lead comes in after hours, gets an immediate
                  response, is qualified, routed, and followed up with before
                  your competitor calls them back.
                </p>
                <div className="space-y-3 bg-viridia-black rounded-xl p-4 border border-gray-800" data-id="element-662" data-mp="1aubt4i8mjmd7">
                  <div className="text-xs text-gray-500 mb-1" data-id="element-663" data-mp="15v8mwugwra3n">
                    Viridia AI Response (Instant)
                  </div>
                  <div className="text-sm text-white" data-id="element-664" data-mp="3qivf8xfo3kwd">
                    "Hi there! We offer 24/7 emergency AC repair. What seems to
                    be the issue with your unit?"
                  </div>
                  <div className="mt-2 pt-2 border-t border-gray-800 text-xs text-viridia flex items-center gap-1" data-id="element-665" data-mp="1nz509dbwmbr3">
                    <CheckCircleIcon className="w-3 h-3" data-id="element-666" data-mp="19bbknsmtj98i" /> Lead captured while
                    competitors sleep
                  </div>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.1} data-id="element-667" data-mp="21zz89uv1iap8">
              <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-6 h-full" data-id="element-668" data-mp="26xr2ivj5q8wl">
                <div className="text-sm text-viridia font-medium mb-4" data-id="element-669" data-mp="26vekkidefyjj">
                  Scenario 2: The "On The Job" Missed Call
                </div>
                <p className="text-gray-300 text-sm mb-6" data-id="element-670" data-mp="1wy9y0hfn6127">
                  You are under a sink fixing a leak. Your phone rings. You
                  cannot answer it.
                </p>
                <div className="space-y-3 bg-viridia-black rounded-xl p-4 border border-gray-800" data-id="element-671" data-mp="llh7f9yl7inq">
                  <div className="text-xs text-gray-500 mb-1" data-id="element-672" data-mp="2zr4elyblgsl9">
                    Viridia AI Response (Instant)
                  </div>
                  <div className="text-sm text-white" data-id="element-673" data-mp="2uv6tzftat02o">
                    "Hi, this is [Your Business]. Sorry we missed your call! Are
                    you looking to schedule a plumbing service?"
                  </div>
                  <div className="mt-2 pt-2 border-t border-gray-800 text-xs text-viridia flex items-center gap-1" data-id="element-674" data-mp="2h1fwx6wr78nu">
                    <CheckCircleIcon className="w-3 h-3" data-id="element-675" data-mp="2c34jynb5wi1e" /> Stops them from
                    calling the next plumber
                  </div>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} data-id="element-676" data-mp="2l317umpq9cve">
              <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-6 h-full" data-id="element-677" data-mp="2l16imw7fayli">
                <div className="text-sm text-viridia font-medium mb-4" data-id="element-678" data-mp="1xbvnbu8udwer">
                  Scenario 3: The Tire Kicker
                </div>
                <p className="text-gray-300 text-sm mb-6" data-id="element-679" data-mp="3shelx7ux3rim">
                  Someone fills out a web form asking for a quote, but they
                  aren't ready to buy yet.
                </p>
                <div className="space-y-3 bg-viridia-black rounded-xl p-4 border border-gray-800" data-id="element-680" data-mp="61k113l44qhc">
                  <div className="text-xs text-gray-500 mb-1" data-id="element-681" data-mp="b0wq7uzgi9n3">
                    Viridia AI Nurture Sequence
                  </div>
                  <div className="text-sm text-white" data-id="element-682" data-mp="39jamx68qv8uj">
                    Automated follow-ups on Day 2, Day 5, and Day 14 with
                    helpful tips and a soft CTA.
                  </div>
                  <div className="mt-2 pt-2 border-t border-gray-800 text-xs text-viridia flex items-center gap-1" data-id="element-683" data-mp="3jfdmc4c7otds">
                    <CheckCircleIcon className="w-3 h-3" data-id="element-684" data-mp="3ogkz6ht7oirb" /> Stays top-of-mind
                    without manual effort
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Best Fit Section */}
      <section className="py-20 bg-viridia-surface border-t border-gray-800" data-id="element-685" data-mp="2o6jvjwn7a5hk">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-686" data-mp="2j91ko0pmhg6x">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-687" data-mp="2eha0q90z28pr">
            <ScrollReveal data-id="element-688" data-mp="2effe8qa1y8jk">
              <h2 className="text-3xl font-bold text-white mb-6" data-id="element-689" data-mp="29i6wurwqa8jd">
                Who is this for?
              </h2>
              <p className="text-gray-400 mb-6 leading-relaxed" data-id="element-690" data-mp="24jly6wx4jwx1">
                Our AI Lead Conversion system is designed specifically for home
                service operators who are tired of losing jobs to faster
                competitors.
              </p>
              <ul className="space-y-4" data-id="element-691" data-mp="1vk8w4pbbyamc">
                <li className="flex items-start gap-3" data-id="element-692" data-mp="1qk4fmzaf9fw4">
                  <BuildingIcon className="w-6 h-6 text-viridia flex-shrink-0 mt-0.5" data-id="element-693" data-mp="1qlnuclgfjivs" />
                  <div data-id="element-694" data-mp="3pucjnaqjnad3">
                    <h4 className="font-bold text-white" data-id="element-695" data-mp="3kxcaus2b7tia">
                      Contractors & Home Services
                    </h4>
                    <p className="text-sm text-gray-400" data-id="element-696" data-mp="2tt1akdbf0kvk">
                      HVAC, plumbing, landscaping, roofing, painting, window
                      installers, and pest control.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3" data-id="element-697" data-mp="2k0cbkhx1wzh4">
                  <ZapIcon className="w-6 h-6 text-viridia flex-shrink-0 mt-0.5" data-id="element-698" data-mp="2ozyqcvsj2ns1" />
                  <div data-id="element-699" data-mp="2b18qx9i8kc0i">
                    <h4 className="font-bold text-white" data-id="element-700" data-mp="2azxgiquac60f">
                      Businesses Running Ads
                    </h4>
                    <p className="text-sm text-gray-400" data-id="element-701" data-mp="2fy8pz0go4ei4">
                      If you are paying for clicks, you need a system to ensure
                      those clicks turn into conversations instantly.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3" data-id="element-702" data-mp="3vi537tg131j9">
                  <PhoneIcon className="w-6 h-6 text-viridia flex-shrink-0 mt-0.5" data-id="element-703" data-mp="4bke4f9ja3ov" />
                  <div data-id="element-704" data-mp="1ostm83e0j78u">
                    <h4 className="font-bold text-white" data-id="element-705" data-mp="1ou4r66j72l1j">Busy Operators</h4>
                    <p className="text-sm text-gray-400" data-id="element-706" data-mp="1jua0o43bzxh0">
                      Owners who are often on the jobsite and cannot always
                      answer the phone immediately.
                    </p>
                  </div>
                </li>
              </ul>
            </ScrollReveal>
            <ScrollReveal delay={0.2} data-id="element-707" data-mp="27jb39aphgeed">
              <div className="bg-viridia-black border border-gray-800 rounded-2xl p-8" data-id="element-708" data-mp="27i9n4rtm11ps">
                <h3 className="text-xl font-bold text-white mb-4" data-id="element-709" data-mp="23gxyopmh2mj6">
                  Related Services
                </h3>
                <div className="space-y-4" data-id="element-710" data-mp="2hgz9awsjh8bu">
                  <Link to="/crm-business-tools" className="block p-4 bg-viridia-surface rounded-xl border border-gray-800 hover:border-viridia/50 transition-colors" data-id="element-711" data-mp="2cixgvl983wnp">
                    <div className="font-bold text-white mb-1" data-id="element-712" data-mp="i5oc26rmgzbs">
                      CRM & Business Tools
                    </div>
                    <div className="text-sm text-gray-400" data-id="element-713" data-mp="3n7g4uj0ze2be">
                      Organize your pipeline and track every lead our AI
                      captures.
                    </div>
                  </Link>
                  <Link to="/ad-campaigns" className="block p-4 bg-viridia-surface rounded-xl border border-gray-800 hover:border-viridia/50 transition-colors" data-id="element-714" data-mp="3edkwz38h5xs2">
                    <div className="font-bold text-white mb-1" data-id="element-715" data-mp="2ziwsnset95e1">
                      Ad Campaigns
                    </div>
                    <div className="text-sm text-gray-400" data-id="element-716" data-mp="2vi36b3h0nyn9">
                      Drive high-intent traffic into your new automated
                      conversion system.
                    </div>
                  </Link>
                  <Link to="/blog/ai-receptionist-vs-human-receptionist-cost" className="block p-4 bg-viridia-surface rounded-xl border border-gray-800 hover:border-viridia/50 transition-colors" data-id="element-717" data-mp="tp6ngugv7rsl">
                    <div className="font-bold text-white mb-1" data-id="element-718" data-mp="118o7e8y0ujpo">
                      AI vs. Human Receptionist: Cost Comparison
                    </div>
                    <div className="text-sm text-gray-400" data-id="element-719" data-mp="160ppibhvf4nm">
                      See the real cost breakdown before you decide how to
                      staff your front line.
                    </div>
                  </Link>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-viridia-black border-t border-gray-800" data-id="element-720" data-mp="n8de6z98ot1c">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-721" data-mp="rbjl8946eo8v">
          <ScrollReveal data-id="element-722" data-mp="r8ozme9fexdv">
            <div className="text-center mb-12" data-id="element-723" data-mp="1yovbf1knuzp1">
              <h2 className="text-3xl font-bold text-white mb-4" data-id="element-724" data-mp="1yo270796cma2">
                Frequently Asked Questions
              </h2>
            </div>
          </ScrollReveal>

          <div className="space-y-4" data-id="element-725" data-mp="3knjbvjf0qfz">
            {faqs.map((faq, index) => <ScrollReveal key={index} delay={index * 0.1} data-id="element-726" data-mp="3us9k9ger2et5" data-mpi={index}>
                <details className="group bg-viridia-surface border border-gray-800 rounded-xl overflow-hidden [&_summary::-webkit-details-marker]:hidden" data-id="element-727" data-mp="3utud91lf1pow">
                  <summary className="flex items-center justify-between p-6 cursor-pointer text-white font-medium" data-id="element-728" data-mp="7o3fyrvhiqso">
                    {faq.question}
                    <ChevronDownIcon className="w-5 h-5 text-gray-400 transition-transform group-open:-rotate-180" data-id="element-729" data-mp="3g0hf1x8o6h55" />
                  </summary>
                  <div className="px-6 pb-6 text-gray-400 text-sm leading-relaxed" data-id="element-730" data-mp="3ptouyc11dfjh">
                    {faq.answer}
                  </div>
                </details>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-viridia-surface border-t border-gray-800" data-id="element-731" data-mp="2sdj5ljiyw9gq">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-732" data-mp="2sbexsqhy6qb6">
          <ScrollReveal data-id="element-733" data-mp="17txdzdt30nhl">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-734" data-mp="1clxcd1ti78c2">
              See where your leads are leaking
            </h2>
            <p className="text-xl text-gray-400 mb-10" data-id="element-735" data-mp="yvlgqsxpmrg6">
              Book a strategy call to see how our AI can start converting your
              leads into booked jobs.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-736" data-mp="1riy5n9rcfbcn">
              <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-base font-medium text-white bg-viridia rounded-lg hover:bg-viridia-dark transition-all duration-300 hover:shadow-[0_0_20px_rgba(16,185,129,0.4)]" data-id="element-737" data-mp="1vkjbwc9dgrdv">
                Request a Lead Flow Review
              </Link>
              <Link to="/pricing" className="inline-flex items-center justify-center px-8 py-4 text-base font-medium text-white bg-viridia-black border border-gray-700 rounded-lg hover:bg-gray-800 transition-colors" data-id="element-738" data-mp="64jz8l6jojth">
                View Packages
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}